/**
 * 伴学助手快捷知识点池（中英文）。展示时由界面随机取 3 条，无序号。
 * @typedef {{ categoryZh: string, questionZh: string, detailZh: string, categoryEn: string, questionEn: string, detailEn: string }} AiQuickKnowledgeRow
 * @type {AiQuickKnowledgeRow[]}
 */
export const AI_QUICK_KNOWLEDGE_PROMPTS = [
  {
    categoryZh: '经济学知识',
    questionZh: '什么是宏观经济的市场解析',
    detailZh: '宏观经济市场解析是从国家层面分析整体经济运行规律，核心指标包括 GDP、CPI、失业率、利率和汇率。它研究总供给与总需求的平衡，分析货币政策（如央行加息 / 降息）、财政政策（如税收、政府支出）对经济的影响。掌握它能帮助理解市场波动、通胀压力和经济周期，对青少年理解商业世界、投资逻辑有基础作用。',
    categoryEn: 'Economics',
    questionEn: 'What is Macroeconomic Market Analysis',
    detailEn: 'Macroeconomic market analysis examines the laws of overall economic operation at the national level. Its core indicators include GDP, CPI, unemployment rate, interest rate, and exchange rate. It studies the balance between aggregate supply and aggregate demand, as well as the impacts of monetary policy (e.g., central bank interest rate hikes/cuts) and fiscal policy (e.g., taxation, government expenditure). Mastery of this knowledge helps understand market fluctuations, inflationary pressures, and economic cycles.'
  },
  {
    categoryZh: '生化知识',
    questionZh: '什么是酸碱中和反应',
    detailZh: '酸碱中和反应是酸与碱相互作用生成盐和水的化学反应，本质是氢离子（H⁺）和氢氧根离子（OH⁻）结合生成水。比如胃酸过多时服用碱性药物（如氢氧化铝），就是利用中和反应缓解不适。它是生物体内维持酸碱平衡的基础，也是很多工业和生活场景的核心反应。',
    categoryEn: 'Biochemistry',
    questionEn: 'What is an Acid-Base Neutralization Reaction',
    detailEn: 'An acid-base neutralization reaction is a chemical reaction in which an acid and a base interact to produce salt and water. Its essence is the combination of hydrogen ions (H⁺) and hydroxide ions (OH⁻) to form water. For example, taking alkaline medicine (e.g., aluminum hydroxide) for excessive gastric acid relieves discomfort through neutralization. It underpins acid-base balance in organisms and is essential in industrial and daily applications.'
  },
  {
    categoryZh: '文学知识',
    questionZh: '什么是叙事视角',
    detailZh: '叙事视角是作者讲述故事的角度，常见的有第一人称（“我” 的视角，代入感强）、第三人称全知视角（作者知道所有角色的想法和事件）、第三人称有限视角（仅知道部分角色的想法）。不同视角会影响读者对故事的理解和情感共鸣，是文学分析的基础概念。',
    categoryEn: 'Literature',
    questionEn: 'What is Narrative Perspective',
    detailEn: 'Narrative perspective refers to the angle from which a story is narrated. Common types include the first-person perspective (using "I", strong sense of immersion), third-person omniscient perspective (the narrator knows all characters’ thoughts and events), and third-person limited perspective (only partial thoughts are revealed). Different perspectives shape readers’ understanding and emotional resonance.'
  },
  {
    categoryZh: '历史知识',
    questionZh: '什么是工业革命的核心影响',
    detailZh: '工业革命始于 18 世纪的英国，以蒸汽机的发明和应用为标志，推动了机器生产取代手工劳动，工厂制度取代手工工场。它极大提高了生产力，改变了社会结构（资产阶级和无产阶级崛起），也推动了城市化进程和全球贸易格局的变化，是现代文明的重要转折点。',
    categoryEn: 'History',
    questionEn: 'Core Impacts of the Industrial Revolution',
    detailEn: 'The Industrial Revolution originated in Britain in the 18th century, marked by the invention and application of the steam engine. It replaced manual labor with machine production and cottage workshops with the factory system. It greatly boosted productivity, reshaped social structure (rise of the bourgeoisie and proletariat), accelerated urbanization, and transformed the global trade pattern, representing a major turning point in modern civilization.'
  },
  {
    categoryZh: '物理知识',
    questionZh: '什么是牛顿三大定律',
    detailZh: '牛顿三大定律是经典力学的基础：第一定律（惯性定律）指出物体不受力时保持静止或匀速直线运动；第二定律指出加速度与合力成正比、与质量成反比（F=ma）；第三定律指出作用力与反作用力大小相等、方向相反。它们能解释生活中很多力学现象，比如推箱子、抛物体的运动规律。',
    categoryEn: 'Physics',
    questionEn: 'What are Newton’s Three Laws of Motion',
    detailEn: 'Newton’s Three Laws form the foundation of classical mechanics:\n- The First Law (Law of Inertia): An object remains at rest or in uniform linear motion without external force.\n- The Second Law: Acceleration is proportional to net force and inversely proportional to mass (F=ma).\n- The Third Law: Action and reaction forces are equal in magnitude and opposite in direction.'
  },
  {
    categoryZh: '环境工程知识',
    questionZh: '什么是污水处理的基本流程',
    detailZh: '污水处理的基本流程分为三级：一级处理（物理处理，去除悬浮物和浮渣）、二级处理（生物处理，去除有机物）、三级处理（深度处理，去除氮、磷等污染物），处理后的水可以排放或回用。理解它能帮青少年认识水资源保护和可持续发展的重要性。',
    categoryEn: 'Environmental Engineering',
    questionEn: 'Basic Process of Wastewater Treatment',
    detailEn: 'Wastewater treatment generally consists of three stages: primary treatment (physical removal of suspended solids and scum), secondary treatment (biological removal of organic matter), and tertiary treatment (advanced removal of nitrogen, phosphorus, and other pollutants). Treated water can be safely discharged or reused.'
  },
  {
    categoryZh: '航空航天知识',
    questionZh: '什么是火箭的推力原理',
    detailZh: '火箭的推力原理基于牛顿第三定律：火箭发动机燃烧燃料产生高温高压气体，高速向后喷出，气体对火箭产生一个大小相等、方向相反的反作用力，推动火箭向前飞行。推力大小与气体喷出速度和质量流量有关，理解它能帮青少年认识航天发射的基本原理。',
    categoryEn: 'Aerospace',
    questionEn: 'Thrust Principle of a Rocket',
    detailEn: 'The thrust principle of a rocket is based on Newton’s Third Law. The rocket engine burns fuel to produce high-temperature, high-pressure gas ejected backward at high speed, generating an equal and opposite reaction force that propels the rocket forward. Thrust depends on exhaust velocity and mass flow rate.'
  },
  {
    categoryZh: '计算机知识',
    questionZh: '什么是算法的时间复杂度',
    detailZh: '时间复杂度是衡量算法运行效率的指标，用大 O 符号表示，描述算法运行时间随输入规模增长的变化趋势。比如 O (1) 表示常数时间（如直接访问数组元素），O (n) 表示线性时间（如遍历数组），O (n²) 表示平方时间（如双重循环）。理解它能帮青少年设计更高效的程序。',
    categoryEn: 'Computer Science',
    questionEn: 'What is Time Complexity of an Algorithm',
    detailEn: 'Time complexity measures the efficiency of an algorithm, expressed using Big O notation, describing how running time grows with input size. Examples: O(1) (constant time, direct array access), O(n) (linear time, array traversal), O(n²) (quadratic time, nested loops).'
  },
  {
    categoryZh: '心理学知识',
    questionZh: '什么是认知失调理论',
    detailZh: '认知失调理论由费斯汀格提出，指当个体的态度和行为不一致时，会产生心理不适，为了缓解这种不适，人们会改变态度或行为，或者寻找理由合理化自己的行为。比如明知吸烟有害却仍吸烟的人，会通过 “吸烟能缓解压力” 来合理化行为，这是理解人类行为动机的重要理论。',
    categoryEn: 'Psychology',
    questionEn: 'What is Cognitive Dissonance Theory',
    detailEn: 'Proposed by Festinger, cognitive dissonance refers to psychological discomfort arising from inconsistency between one’s attitudes and behaviors. To relieve discomfort, individuals may change attitudes, adjust behaviors, or rationalize actions. For instance, smokers may justify the habit by claiming it relieves stress.'
  },
  {
    categoryZh: '医学基础',
    questionZh: '什么是人体循环系统的运作',
    detailZh: '人体循环系统由心脏、血管和血液组成，分为体循环和肺循环：体循环将含氧血液输送到全身，肺循环将含二氧化碳的血液输送到肺部进行气体交换。心脏是动力泵，通过收缩和舒张推动血液流动，为全身细胞输送氧气和营养，带走代谢废物。',
    categoryEn: 'Basic Medicine',
    questionEn: 'Operation of the Human Circulatory System',
    detailEn: 'The human circulatory system consists of the heart, blood vessels, and blood, divided into systemic circulation and pulmonary circulation. Systemic circulation delivers oxygenated blood throughout the body; pulmonary circulation sends deoxygenated blood to the lungs for gas exchange. The heart acts as a pump, contracting and relaxing to transport oxygen, nutrients, and metabolic wastes.'
  }
]



// WEBPACK FOOTER //
// ./src/assets/js/aiQuickKnowledgePrompts.js